import React, { useEffect, useState } from "react"
import { Outlet, Link, useLocation, useNavigate } from "react-router-dom"
import { getAPIBaseURL, getUser, logout } from "../services/helpers"
import { getData } from "../services/request"
import { needsEmployerOnboarding } from "./employerOnboardingUtils"
import logo from "../asset/images/logo.svg"

const EmployerDashboardLayout = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const user = getUser()
  const [checkingOnboarding, setCheckingOnboarding] = useState(true)

  useEffect(() => {
    const isLoggedIn =
      typeof window !== "undefined" &&
      (sessionStorage.getItem("employme-active") === "1" || !!localStorage.getItem("employmeUser"))

    if (!isLoggedIn) {
      setCheckingOnboarding(false)
      return
    }

    const checkOnboarding = async () => {
      const url = `${getAPIBaseURL()}/employme/employer/company`
      const res = await getData(url, {}, {})
      setCheckingOnboarding(false)
      if (res.error === "" && res.result?.status === 200) {
        const company = res.result.data?.data || res.result.data
        if (needsEmployerOnboarding(company)) {
          navigate("/employer/onboarding", { replace: true })
        }
      }
    }

    checkOnboarding()
  }, [navigate, location.pathname])

  const handleLogout = () => {
    logout()
  }

  const isNavActive = (path, matchPrefix) => {
    const target = matchPrefix || path
    if (path === "/employer/dashboard") {
      return location.pathname === path
    }
    return location.pathname === path || location.pathname.startsWith(`${target}/`)
  }

  const DashboardIcon = ({ className }) => (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0 7-7 7 7M5 10v10a1 1 0 001 1h3m10-11 2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
    </svg>
  )

  const JobsIcon = ({ className }) => (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7h16M4 11h16M4 15h10" />
    </svg>
  )

  const ApplicantsIcon = ({ className }) => (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 12c2.21 0 4-1.79 4-4S14.21 4 12 4 8 5.79 8 8s1.79 4 4 4z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 20a6 6 0 1112 0H6z" />
    </svg>
  )

  const CvScreeningIcon = ({ className }) => (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
    </svg>
  )

  const MessagesIcon = ({ className }) => (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h8M8 14h5M5 20l2-3h10a3 3 0 003-3V7a3 3 0 00-3-3H7a3 3 0 00-3 3v10l1 3z" />
    </svg>
  )

  const ProfileIcon = ({ className }) => (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
    </svg>
  )

  const LogoutIcon = ({ className }) => (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
    </svg>
  )

  const navItems = [
    { path: "/employer/dashboard", label: "Dashboard", shortLabel: "Home", icon: DashboardIcon },
    { path: "/employer/jobs", label: "Jobs", shortLabel: "Jobs", icon: JobsIcon },
    { path: "/employer/applicants", label: "Applicants", shortLabel: "People", icon: ApplicantsIcon },
    {
      path: "/employer/cv-screening",
      label: "CV Screening",
      shortLabel: "Screen",
      icon: CvScreeningIcon,
      matchPrefix: "/employer/cv-screening",
    },
    { path: "/employer/messages", label: "Messages", shortLabel: "Chat", icon: MessagesIcon, matchPrefix: "/employer/messages" },
    { path: "/employer/profile", label: "Profile", shortLabel: "Profile", icon: ProfileIcon },
  ]

  const activeItem = navItems.find((item) => isNavActive(item.path, item.matchPrefix))

  return (
    <div className="min-h-screen bg-gray-50">
      <aside className="fixed top-0 left-0 z-50 hidden h-full w-64 bg-white shadow-lg md:block">
        <div className="flex h-full flex-col">
          <div className="border-b border-gray-200 px-6 py-5">
            <Link to="/employer/dashboard" className="flex items-center gap-2">
              <img className="h-8 w-8 -mt-0.5" src={logo} alt="Employ Me logo" />
              <span className="text-xl font-semibold text-gray-900 leading-none tracking-tight">
                EmployMeGlobal
              </span>
            </Link>
          </div>

          <div className="border-b border-gray-200 px-6 py-4">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gray-200 text-gray-600">
                {user?.organization?.charAt(0)?.toUpperCase() ||
                  user?.companyName?.charAt(0)?.toUpperCase() ||
                  "E"}
              </div>
              <div className="flex-1 min-w-0">
                <p className="truncate text-sm font-medium text-gray-900">
                  {user?.organization || user?.companyName || "Employer"}
                </p>
                <p className="truncate text-xs text-gray-500">{user?.email || ""}</p>
              </div>
            </div>
          </div>

          <nav className="flex-1 space-y-1 px-4 py-4">
            {navItems.map((item) => {
              const IconComponent = item.icon
              const active = isNavActive(item.path, item.matchPrefix)
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center gap-3 rounded-lg px-4 py-3 text-sm font-medium transition-colors ${
                    active
                      ? "bg-black text-white"
                      : "text-gray-700 hover:bg-gray-100"
                  }`}
                >
                  <IconComponent className={`h-5 w-5 ${active ? "text-white" : "text-gray-600"}`} />
                  <span>{item.label}</span>
                </Link>
              )
            })}
          </nav>

          <div className="border-t border-gray-200 p-4">
            <button
              onClick={handleLogout}
              className="flex w-full items-center gap-3 rounded-lg px-4 py-3 text-sm font-medium text-red-600 transition-colors hover:bg-red-50"
            >
              <LogoutIcon className="h-5 w-5 text-red-600" />
              <span>Logout</span>
            </button>
          </div>
        </div>
      </aside>

      <div className="flex min-h-screen flex-col md:ml-64 safe-pb-nav md:pb-0">
        <header className="sticky top-0 z-30 border-b border-gray-200 bg-white/95 backdrop-blur-sm">
          <div className="flex h-14 items-center justify-between gap-3 px-3 sm:h-16 sm:px-4 md:px-6">
            <Link
              to="/employer/dashboard"
              className="flex shrink-0 items-center gap-2 md:hidden"
              aria-label="Employer dashboard home"
            >
              <img className="h-7 w-7" src={logo} alt="" />
            </Link>
            <h1 className="min-w-0 flex-1 text-center text-base font-semibold leading-snug text-gray-900 sm:text-lg md:text-left">
              {activeItem?.label || "Dashboard"}
            </h1>
            <button
              type="button"
              onClick={handleLogout}
              className="shrink-0 rounded-lg p-2.5 text-red-600 hover:bg-red-50 md:hidden"
              aria-label="Log out"
            >
              <LogoutIcon className="h-5 w-5" />
            </button>
            <div className="hidden w-9 md:block" aria-hidden="true" />
          </div>
        </header>

        <main className="mx-auto w-full max-w-6xl flex-1 px-3 py-4 sm:px-4 sm:py-5 md:px-6 md:py-6">
          {checkingOnboarding ? (
            <div className="flex min-h-[40vh] items-center justify-center animate-fade-in">
              <div className="text-center">
                <div className="mx-auto mb-3 h-8 w-8 animate-pulse-soft rounded-full bg-slate-200" />
                <p className="text-sm text-slate-500">Loading your workspace...</p>
              </div>
            </div>
          ) : (
            <Outlet />
          )}
        </main>
      </div>

      <nav
        className="fixed bottom-0 left-0 right-0 z-40 border-t border-gray-200 bg-white pb-[env(safe-area-inset-bottom,0px)] md:hidden"
        aria-label="Employer navigation"
      >
        <div className="mx-auto flex h-[3.75rem] max-w-lg items-stretch justify-around px-1">
          {navItems.map((item) => {
            const IconComponent = item.icon
            const active = isNavActive(item.path, item.matchPrefix)
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex min-w-0 flex-1 flex-col items-center justify-center gap-0.5 px-0.5 py-1.5 transition-colors ${
                  active ? "text-black" : "text-gray-500"
                }`}
              >
                <IconComponent
                  className={`h-5 w-5 shrink-0 ${active ? "text-black" : "text-gray-400"}`}
                />
                <span className="max-w-full whitespace-nowrap text-center text-[10px] font-medium leading-none tracking-tight">
                  {item.shortLabel}
                </span>
              </Link>
            )
          })}
        </div>
      </nav>
    </div>
  )
}

export default EmployerDashboardLayout
